/**
 * Support nodes (04 §2.2), resolved at once with a choice index, and the HQ services the
 * contract has no method for yet. Those ride on the same door under reserved `hq:` ids:
 *   hq:recruit              a replacement recruit for RP
 *   hq:medevac:<rangerId>   Priority Medevac (04 §4.4)
 *   hq:intel:<nodeId>       buy offer `choice` of that node's three intel cards (04 §8); the
 *                           menus use `GameApi.buyIntel`, which this now forwards to
 *   hq:unlock:<unlockId>    spend commendations in the Quartermaster's ledger (04 §5)
 */

import { Pcg32 } from '../sim/rng';
import { enlist, settleOperation } from './aftermath/endings';
import { recoveryStep } from './aftermath/roster';
import { generateRecruit } from './company';
import { hazardRules, UNLOCKS } from './content/career';
import { COMPANY, RARE_ITEMS, REWARD } from './content/economy';
import { MAP } from './content/map';
import { INTEL } from './content/modifiers';
import { stockFind } from './gear';
import { buyIntel, intelOffers } from './intel';
import { advanceTo, openNodes, refreshNodes } from './map';
import { activeOperation, companyRanger, findNode, fullName, type OperationState, type ProfileState } from './state';
import type { RangerRecord, SpecialItem, TheatreNode } from './types';

/** The three recruits a Replacement Depot offers: a pure function of the node, so the menu can show them first. */
export function depotCandidates(op: OperationState, node: TheatreNode): RangerRecord[] {
  const rng = new Pcg32(op.seed, 0x0907).fork(node.id);
  const taken = new Set<string>();
  const rules = hazardRules(op.hazard);
  return Array.from({ length: MAP.depotCandidates }, (_, i) => generateRecruit(rng, `${node.id}-${i}`, taken, rules));
}

function rest(op: OperationState): string {
  let back = 0;
  for (const ranger of op.company) {
    ranger.fatigue = Math.max(0, ranger.fatigue - 1);
    if (recoveryStep(ranger, op)) back += 1;
  }
  return back > 0 ? `Rest & Refit: the company rests; ${back} back on their feet.` : 'Rest & Refit: the company rests.';
}

/** Which rare item this cache holds: a pure function of the node, any of the four on any profile. */
export function cacheItem(op: OperationState, node: TheatreNode): SpecialItem {
  return new Pcg32(op.seed, 0x0909).fork(node.id).pick(RARE_ITEMS);
}

function supply(op: OperationState, node: TheatreNode, choice: number): string {
  if (choice === 0) {
    op.rp += REWARD.supplyRp;
    return `Supply Cache: +${REWARD.supplyRp} RP.`;
  }
  if (choice !== 1) throw new Error('Choose the requisition (0) or the equipment (1).');
  return `Supply Cache: ${stockFind(op.stock, cacheItem(op, node))} added to the operation stock.`;
}

function depot(profile: ProfileState, op: OperationState, node: TheatreNode, choice: number): string {
  if (choice < 0) return 'Replacement Depot: nobody was taken on.';
  if (op.company.length >= COMPANY.cap) return 'Replacement Depot: the company is at full strength; nobody joins.';
  const candidate = depotCandidates(op, node)[choice];
  if (!candidate) throw new Error(`Choose one of the ${MAP.depotCandidates} recruits.`);
  return `Replacement Depot: ${fullName(enlist(profile, op, candidate))} joins the company.`;
}

function intercept(op: OperationState, node: TheatreNode, choice: number): string {
  if (choice === 0) {
    const theatre = Math.min(node.theatre + 1, op.theatres.length - 1);
    if (!op.revealedTheatres.includes(theatre)) op.revealedTheatres.push(theatre);
    return `Signals Intercept: enemy dispositions in theatre ${theatre + 1} are known.`;
  }
  if (choice !== 1) throw new Error('Choose the theatre report (0) or the intel card (1).');
  const card = new Pcg32(op.seed, 0x090a).fork(node.id).pick(INTEL);
  op.intelHeld.push(card.id);
  return `Signals Intercept: ${card.name} will be ready for the next mission.`;
}

/** Visit an open support node. Mission and rescue nodes are entered with `deploy`. */
export function visitNode(profile: ProfileState, nodeId: string, choice: number): string {
  const op = activeOperation(profile);
  if (op.deployed) throw new Error('A ranger is in the field: the company cannot move.');
  const node = findNode(op, nodeId);
  if (!openNodes(op).some((n) => n.id === nodeId)) throw new Error('That node cannot be reached from here.');
  let line: string;
  if (node.kind === 'rest') line = rest(op);
  else if (node.kind === 'supply') line = supply(op, node, choice);
  else if (node.kind === 'depot') line = depot(profile, op, node, choice);
  else if (node.kind === 'intercept') line = intercept(op, node, choice);
  else throw new Error('That is a mission: choose a ranger and deploy.');
  node.state = 'done';
  op.log.push(line);
  advanceTo(op, node, op.company.some((r) => r.status === 'pow'));
  settleOperation(profile, op);
  return line;
}

/** The `hq:` services. */
export function hqService(profile: ProfileState, id: string, choice: number): string {
  const [, service, ...parts] = id.split(':');
  const arg = parts.join(':');
  if (service === 'unlock') {
    const unlock = UNLOCKS.find((u) => u.id === arg);
    if (!unlock) throw new Error('No such unlock.');
    if (profile.unlocks.includes(unlock.id)) throw new Error(`${unlock.name} is already unlocked.`);
    if (profile.commendations < unlock.cost) throw new Error(`${unlock.name} costs ${unlock.cost} commendations.`);
    profile.commendations -= unlock.cost;
    profile.unlocks.push(unlock.id);
    return `Unlocked: ${unlock.name}.`;
  }
  const op = activeOperation(profile);
  if (op.deployed) throw new Error('A ranger is in the field: HQ is busy.');
  const pay = (cost: number, what: string): void => {
    if (op.rp < cost) throw new Error(`${what} costs ${cost} RP; the company has ${op.rp}.`);
    op.rp -= cost;
  };
  let line: string;
  if (service === 'recruit') {
    if (op.company.length >= COMPANY.cap) throw new Error('The company is at full strength.');
    pay(COMPANY.recruitCost, 'A replacement');
    line = `${fullName(enlist(profile, op))} reports for duty (-${COMPANY.recruitCost} RP).`;
  } else if (service === 'medevac') {
    const ranger = companyRanger(op, arg);
    const inHospital = ranger.status === 'wounded' || ranger.status === 'critical';
    if (!inHospital) throw new Error(`${fullName(ranger)} is not in hospital.`);
    pay(COMPANY.medevacCost, 'Priority Medevac');
    recoveryStep(ranger, op);
    line = `Priority Medevac for ${fullName(ranger)} (-${COMPANY.medevacCost} RP).`;
  } else if (service === 'intel') {
    const offer = intelOffers(findNode(op, arg))[choice];
    if (!offer) throw new Error('That intel is not on offer for this mission.');
    return buyIntel(profile, arg, offer);
  } else throw new Error(`Unknown HQ service: ${id}.`);
  op.log.push(line);
  refreshNodes(op);
  return line;
}
